import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import usePageMeta from "@/lib/usePageMeta";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Mail } from "lucide-react";
import { COMPANY } from "@/legal/companyInfo";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

export default function Contact() {
  const { t, i18n } = useTranslation();
  usePageMeta("contact");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { name, email, subject, message, lang: (i18n.language || "fr").slice(0, 2) });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      <PublicHeader />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-14 page-enter">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl sm:text-5xl leading-tight text-center">
          {t("contact_page.title")}
        </motion.h1>
        <p className="mt-3 text-text-secondary text-base sm:text-lg text-center">{t("contact_page.subtitle")}</p>

        {sent ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="card-c mt-8 text-center py-10 border-2 border-primary-c">
            <CheckCircle2 className="w-12 h-12 text-primary-c mx-auto" />
            <h2 className="font-display text-2xl mt-4">{t("contact_page.sent_title")}</h2>
            <p className="text-text-secondary text-sm mt-2">{t("contact_page.sent_msg")} <strong className="text-[var(--text)]">{email}</strong>.</p>
            <Link to="/" className="btn-primary inline-block mt-6">{t("reserver.back_home")}</Link>
          </motion.div>
        ) : (
          <form onSubmit={onSubmit} className="card-c mt-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("contact_page.name")}</label>
                <input data-testid="contact-name-input" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="input-c" placeholder={t("auth.name_placeholder")} />
              </div>
              <div>
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("contact_page.email")}</label>
                <input data-testid="contact-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-c" placeholder={t("auth.email_placeholder")} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2">{t("contact_page.subject")} <span className="text-text-secondary font-normal">({t("contact_page.optional")})</span></label>
              <input data-testid="contact-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="input-c" placeholder={t("contact_page.subject_placeholder")} />
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2">{t("contact_page.message")}</label>
              <textarea data-testid="contact-message-input" required minLength={10} value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className="input-c" style={{ resize: "vertical" }} placeholder={t("contact_page.message_placeholder")} />
            </div>
            <button data-testid="contact-submit-button" disabled={loading} className="btn-primary w-full">
              {loading ? t("contact_page.sending") : t("contact_page.submit")}
            </button>
          </form>
        )}

        <div className="mt-10 flex items-start gap-3 text-sm text-text-secondary">
          <Mail className="w-4 h-4 mt-0.5 text-primary-c" />
          <div>
            {t("contact_page.direct_email")} <a className="text-primary-c font-semibold" href={`mailto:${COMPANY.contactEmail}`}>{COMPANY.contactEmail}</a>.
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
